import Quill from 'quill';
import ToolbarModule from 'quill/modules/toolbar';

import { QuillTemplate } from '../../../../generated/graphql.generated';

import { createDropdownMenu, positionDropdown } from './common-toolbar-utils';
import { VariableToolbarQuillModule } from './variableToolbar.quillModule';

export interface QuillTemplateDefinition {

  id?: string;

  name: string;

  // Expecting a Quill Delta JSON
  contents: string;

  order?: number;

  createdAt?: string;
}

export class TemplateToolbarQuillModule {
  private quill: Quill;
  private templateButton: HTMLButtonElement | null = null;
  private templateMenu: HTMLDivElement | null = null;
  private templates: QuillTemplate[] = [];
  public onTemplateSelected?: (template: QuillTemplate) => void;

  constructor(quill: Quill) {
    this.quill = quill;
    this.addTemplateButton();
    document.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('click', this.handleOutsideClick);
  }

  private addTemplateButton(): void {
    const toolbar = this.getToolbarModule()?.container;
    if (!toolbar) return;

    this.templateButton = document.createElement('button');
    this.templateButton.innerHTML = '📄';
    this.templateButton.classList.add('ql-template-button');
    this.templateButton.addEventListener('click', () => this.toggleMenu());

    toolbar.querySelector('.ql-formats')?.appendChild(this.templateButton);
  }

  private toggleMenu(): void {
    const editorContainer = this.quill.root.parentElement as HTMLElement;
    if (!editorContainer) return;

    if (this.templateMenu) {
      this.templateMenu.remove();
      this.templateMenu = null;
    } else {
      this.templateMenu = createDropdownMenu(
        this.templates.map((t) => t.name),
        (selected) => {
          const template = this.templates.find((t) => t.name === selected);
          if (template) this.applyTemplate(template);
        },
        '/business/templates', // Link to add new templates
        'No templates found. Click here to add.'
      );

      document.body.appendChild(this.templateMenu);
      positionDropdown(this.templateMenu, this.templateButton!, document.body);
    }
  }

  private applyTemplate(template?: QuillTemplate): void {
    const editorText = this.quill.getText().trim();
    if (editorText.length > 0) {
      const confirmed = window.confirm(
        "Applying this template will overwrite existing content. Are you sure?"
      );
      if (!confirmed) return;
    }

    try {
      // Parse the stored Quill Delta JSON
      const delta = JSON.parse(template.contents);
      this.quill.setContents(delta, Quill.sources.USER);

      // Update the template variables with actual values
      const variableToolbar = this.quill?.getModule('variableToolbar') as VariableToolbarQuillModule;
      if (variableToolbar) {
        variableToolbar.refreshVariableValuesInContents();
      }
      // Fire callback/event when a template is selected
      if (template && this.onTemplateSelected) {
        this.onTemplateSelected(template);
      }
    } catch (error) {
      console.error('Invalid Quill Delta JSON:', error);
      alert('Error applying template: Invalid format');
    }
  }

  public updateTemplates(templates: QuillTemplate[]): void {
    this.templates = templates;
  }

  private handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.templateMenu) {
      this.templateMenu.remove();
      this.templateMenu = null;
    }
  };

  private handleOutsideClick = (event: MouseEvent): void => {
    if (
      this.templateMenu &&
      !this.templateMenu.contains(event.target as Node) &&
      this.templateButton !== event.target
    ) {
      this.templateMenu.remove();
      this.templateMenu = null;
    }
  };

  private getToolbarModule() {
    return this.quill.getModule('toolbar') as ToolbarModule;
  }
}
